// ---------------------------------------------------------------------------
// TextArea and CharCount, ported from the design system (R10)
//
// <sdr-char-count>: "120 / 160", the counter on its own, for an editor that
// is not a TextArea. Caution from `cautionAt` of `max` (default 0.9), with
// the alert icon; critical over the limit. The figures are hidden from
// screen readers, who hear a sentence ("40 of 160 characters left") through
// the editor's aria-describedby (the counter's id) and, politely, in a live
// region: at once when a threshold is crossed, otherwise after typing pauses
// for `delay` ms (default 1,000), never below caution, never on the first
// render, and not when only the threshold moved (count and max unchanged).
// The same words twice get an invisible no-break space, so they are said
// again. The region is written directly, as the system's useAnnounce does;
// the template never touches its text. The element itself makes no box
// (display: contents, unless the page gives it a display), so the counter
// span is laid out in the page as the system's is, and countClass styles it.
//
// <sdr-text-area>: TextField's multi-line sibling in the same field (label,
// hint or error, `optional`, `size: 'sm'` on the field). It grows with its
// text from `minRows` (default 3) to `maxRows` (default 12; null or 0: no
// cap), then scrolls. As in the system it measures a hidden twin of the same
// width after every render (here: updated), again once the fonts are ready,
// and whenever its width changes (a ResizeObserver, if there is one); the
// observer goes when the element leaves the page and comes back with it (a
// move in the page). `maxLength` is counted,
// not enforced: a CharCount sits at the end of the hint row, and over the
// limit the field turns invalid; the text stays.
//
// Both are light-DOM Lit elements because the system's components hold
// state and effects. A TextArea is controlled by `value` (it reports each
// edit through onChange and shows what it is given) or starts at
// `defaultValue` and keeps its own text, as the system's useState does.
// Controlled, React draws the page's answer to an edit and then restores
// the field, both at the end of the event. A Lit page answers in its own
// updates, a microtask per level, so the port checks once they are done, in
// the next frame (before it is painted; a 0 ms timer where there are no
// frames): an edit the page took is never written back, so the caret stays
// where the person types; one it refused is undone.
//
// onChange(event) is called on every input, as React's onChange is, with
// the native event; event.target.value is the text. The text is the
// textarea's content (defaultValue) and its value property, written in
// React's order (areaText): at mount the content, then the value; later the
// value first when it differs, then the content. In Chrome that order is
// the caret: a text the page changes before anything is typed goes on at
// its end, as in the system, not at its start (R10d review).
//
// Renames, because an element would shadow its own HTML attributes:
//   CharCount: id → countId, className → countClass.
//   TextArea: id → areaId, className → areaClass, aria-describedby →
//   describedBy, aria-label → ariaLabelText. Of the textarea attributes
//   React passes through, these are properties here: name, placeholder,
//   disabled, readOnly, required, autoComplete → autocomplete, onFocus,
//   onBlur, onKeyDown → onKeydown (as field.ts names it). Any other
//   attribute React would pass on (autoFocus, spellCheck, minLength, wrap,
//   cols, form, title) is not taken; spellcheck, lang, dir and title can sit
//   on the element itself, which the textarea inherits or shows.
//
// The field around the TextArea is the one of field.ts, written here again
// because field.ts does not export it.
// ---------------------------------------------------------------------------

import { html, LitElement, noChange, type PropertyValues, type TemplateResult } from 'lit';
import { Directive, directive, type ElementPart, type PartInfo, PartType } from 'lit/directive.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';

import { fieldId } from './field.js';
import { cx, noBox, node, num, present } from './helpers.js';
import { icon } from './icons.js';

/**
 * The text of a textarea React controls, written as ReactDOMTextarea writes it (R10d review): at mount the content
 * (defaultValue), then the value property with the same text; on an update the value first, when it differs, then
 * the content. In Chrome the order is the caret. A value write that changes the text puts the caret after it, so a
 * text the page changes before anything is typed goes on at its end; a write of the text the field already shows
 * leaves the caret where it is (at the start, after the mount). The content first, as `.defaultValue` then
 * `live()` wrote it, changes a clean field without a value write, and the caret stays at the start.
 */
class AreaText extends Directive {
  private mounted = false;

  constructor(info: PartInfo) {
    super(info);
    if (info.type !== PartType.ELEMENT) throw new Error('areaText() belongs on the <textarea> itself');
  }

  render(_text: string) {
    return noChange;
  }

  override update(part: ElementPart, [text]: [string]) {
    const area = part.element as HTMLTextAreaElement;
    if (!this.mounted) {
      this.mounted = true;
      area.defaultValue = text;
      if (text !== '') area.value = text;
    } else {
      if (area.value !== text) area.value = text;
      if (area.defaultValue !== text) area.defaultValue = text;
    }
    return noChange;
  }
}
const areaText = directive(AreaText);

/** A no-break space, spelled out: the formatter would write it as an invisible character. */
const NO_BREAK = String.fromCharCode(0xa0);

/**
 * The system's useAnnounce: the words go straight into a polite live region, with the keystroke that caused
 * them. The same words twice differ invisibly (a no-break space), so they are said again.
 */
export function announce(region: Element | null | undefined, text: string): void {
  if (region) region.textContent = region.textContent === text ? text + NO_BREAK : text;
}

/** "1 character", "12 characters": the count in the reader's locale and the word that fits it. */
export function plural(n: number, one: string, many: string): string {
  return `${num(n)} ${n === 1 ? one : many}`;
}

/** What a screen reader hears: "40 of 160 characters left", "5 characters over the limit of 160". */
export function countSentence(count: number, max: number): string {
  return count <= max
    ? `${num(max - count)} of ${num(max)} characters left`
    : `${plural(count - max, 'character', 'characters')} over the limit of ${num(max)}`;
}

export type CountTone = 'normal' | 'caution' | 'critical';

export interface CharCountProps {
  /** The count; or `value`, whose length is counted. */
  count?: number;
  value?: string | null;
  max: number;
  /** Share of max where caution starts (default 0.9). Over max is critical. */
  cautionAt?: number;
  /** The sentence screen readers hear, for German in the admin area. */
  describe?: (count: number, max: number) => string;
  /** Pause in ms before a changed count near or over the limit is announced (default 1000). */
  delay?: number;
  /** Id of the sentence: put it in the editor's aria-describedby. */
  id?: string;
  className?: string;
}

interface CountLook {
  count: number;
  tone: CountTone;
  text: string;
}

function countLook(p: CharCountProps): CountLook {
  const max = p.max;
  const count = p.count != null ? p.count : String(p.value == null ? '' : p.value).length;
  const cautionAt = p.cautionAt == null ? 0.9 : p.cautionAt;
  const tone: CountTone = count > max ? 'critical' : count >= max * cautionAt ? 'caution' : 'normal';
  return { count, tone, text: (p.describe || countSentence)(count, max) };
}

/** The counter's markup; `region` receives its live region. */
function countView(p: CharCountProps, look: CountLook, region: (el?: Element) => void): TemplateResult {
  return html`<span class=${cx('sdr-count', `sdr-count--${look.tone}`, p.className)}
    ><span class="sdr-count__value" aria-hidden="true"
      >${look.tone === 'normal' ? null : icon({ name: 'alert', size: 12 })}${`${num(look.count)} / ${num(p.max)}`}</span
    ><span class="sdr-visually-hidden" id=${ifDefined(p.id)}>${node(look.text)}</span
    ><span class="sdr-visually-hidden" role="status" ${ref(region)}></span
  ></span>`;
}

/**
 * The system's CharCount effect on [count, max, tone], run after each render: a change of tone is announced at
 * once; any other change of count or max near or over the limit after the pause, which a newer change cancels.
 */
class CountAnnouncer {
  private region: Element | undefined;
  private seen: { tone: CountTone; count: number; max: number } | null = null;
  private deps: [number, number, CountTone] | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;

  readonly into = (el?: Element) => {
    this.region = el;
  };

  after(look: CountLook, max: number, delay: number | undefined): void {
    const deps: [number, number, CountTone] = [look.count, max, look.tone];
    const last = this.deps;
    if (last && deps.every((d, i) => Object.is(d, last[i]))) return;
    this.deps = deps;
    this.stop();
    const prev = this.seen;
    this.seen = { tone: look.tone, count: look.count, max };
    // Nothing on the first render, nor when only the threshold moved
    if (!prev || (prev.count === look.count && prev.max === max)) return;
    if (prev.tone !== look.tone) {
      announce(this.region, look.text);
      return;
    }
    if (look.tone === 'normal') return;
    const text = look.text;
    this.timer = setTimeout(() => announce(this.region, text), delay == null ? 1000 : delay);
  }

  /** The effect's cleanup: a pending announcement goes. */
  stop(): void {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  /** The counter left the page (React unmounts it): the next one starts afresh. */
  reset(): void {
    this.stop();
    this.seen = null;
    this.deps = undefined;
  }
}

export class SdrCharCount extends LitElement {
  static override properties = {
    count: { type: Number },
    value: {},
    max: { type: Number },
    cautionAt: { type: Number },
    describe: { attribute: false },
    delay: { type: Number },
    countId: { attribute: false },
    countClass: { attribute: false },
  };

  declare count: number | undefined;
  declare value: string | null | undefined;
  declare max: number;
  declare cautionAt: number | undefined;
  declare describe: ((count: number, max: number) => string) | undefined;
  declare delay: number | undefined;
  /** The system's id: the sentence's id, for the editor's aria-describedby. */
  declare countId: string | undefined;
  declare countClass: string | undefined;

  private readonly announcer = new CountAnnouncer();

  override createRenderRoot() {
    return this;
  }

  private props(): CharCountProps {
    return {
      count: this.count,
      value: this.value,
      max: this.max,
      cautionAt: this.cautionAt,
      describe: this.describe,
      id: this.countId,
      className: this.countClass,
    };
  }

  override connectedCallback() {
    super.connectedCallback();
    // The system's counter is an inline-flex span in the page. As an inline box of its own around it, the element
    // would add a line in the page's line height (22px for 16px in Chrome), so it makes none, unless the page set one
    noBox(this);
  }

  protected override updated(changed: PropertyValues<this>) {
    super.updated(changed);
    this.announcer.after(countLook(this.props()), this.max, this.delay);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.announcer.stop();
  }

  override render() {
    const p = this.props();
    return countView(p, countLook(p), this.announcer.into);
  }
}

interface FieldFrame {
  id: string;
  label?: unknown;
  hint?: unknown;
  error?: unknown;
  optional?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/** The system's Field: label, control, then the error (as the fix, with the alert icon) or the hint. */
function field(p: FieldFrame, control: unknown): TemplateResult {
  const message = present(p.error)
    ? html`<div class="sdr-field__error" id=${`${p.id}-msg`}>${icon({ name: 'alert', size: 12 })}${node(p.error)}</div>`
    : present(p.hint)
      ? html`<div class="sdr-field__hint" id=${`${p.id}-msg`}>${node(p.hint)}</div>`
      : null;
  return html`<div
    class=${cx('sdr-field', p.size === 'sm' && 'sdr-field--sm', present(p.error) && 'sdr-field--invalid', p.className)}
  >${
    present(p.label)
      ? html`<label class="sdr-field__label" for=${p.id}
          >${node(p.label)}${p.optional ? html`<span class="sdr-field__opt"> optional</span>` : null}</label
        >`
      : null
  }${control}${message}</div>`;
}

export class SdrTextArea extends LitElement {
  static override properties = {
    label: { attribute: false },
    hint: { attribute: false },
    error: { attribute: false },
    optional: { type: Boolean },
    size: {},
    value: { attribute: false },
    defaultValue: { attribute: false },
    onChange: { attribute: false },
    minRows: { type: Number },
    maxRows: { attribute: false },
    maxLength: { type: Number },
    cautionAt: { type: Number },
    describeCount: { attribute: false },
    describedBy: { attribute: false },
    areaId: { attribute: false },
    areaClass: { attribute: false },
    ariaLabelText: { attribute: false },
    name: {},
    placeholder: {},
    disabled: { type: Boolean },
    readOnly: { type: Boolean },
    required: { type: Boolean },
    autocomplete: {},
    onFocus: { attribute: false },
    onBlur: { attribute: false },
    onKeydown: { attribute: false },
    own: { state: true },
  };

  /** Always; placeholder text is not a label. */
  declare label: unknown;
  declare hint: unknown;
  /** The fix, in words; it replaces the hint. */
  declare error: unknown;
  declare optional: boolean;
  /** 'sm' marks the field small, as the system's Field does. */
  declare size: 'sm' | 'md' | undefined;
  /** Makes it controlled: it shows this text and reports edits through onChange. */
  declare value: string | number | null | undefined;
  declare defaultValue: string | number | null | undefined;
  declare onChange: ((event: Event) => void) | undefined;
  /** Lines while the text is short (default 3). */
  declare minRows: number | undefined;
  /** It grows to this many lines, then scrolls (default 12; null: no cap). */
  declare maxRows: number | null | undefined;
  /** Shows a CharCount beside the hint and turns the field invalid over it. Counted, not enforced. */
  declare maxLength: number | undefined;
  /** Share of maxLength where the counter turns caution (default 0.9). */
  declare cautionAt: number | undefined;
  declare describeCount: ((count: number, max: number) => string) | undefined;
  /** More ids for the textarea's aria-describedby, after the message and the counter (the system's aria-describedby). */
  declare describedBy: string | undefined;
  /** The system's id: the textarea's id; the label and the messages refer to it. */
  declare areaId: string | undefined;
  /** The system's className, on the field. */
  declare areaClass: string | undefined;
  /** The system's aria-label, on the textarea, for a field without a visible label. */
  declare ariaLabelText: string | undefined;
  declare name: string | undefined;
  /** An example ("e.g. Heads of operations at …"), never the label. */
  declare placeholder: string | undefined;
  declare disabled: boolean;
  declare readOnly: boolean;
  declare required: boolean;
  declare autocomplete: string | undefined;
  declare onFocus: ((event: FocusEvent) => void) | undefined;
  declare onBlur: ((event: FocusEvent) => void) | undefined;
  declare onKeydown: ((event: KeyboardEvent) => void) | undefined;
  protected declare own: string | undefined;

  private readonly autoId = fieldId();
  private readonly area = createRef<HTMLTextAreaElement>();
  private readonly twin = createRef<HTMLTextAreaElement>();
  private readonly counter = new CountAnnouncer();
  private observer: ResizeObserver | undefined;
  /** Cancels the pending check that the given text is shown again (controlled). */
  private hold: (() => void) | undefined;

  constructor() {
    super();
    this.optional = false;
    this.disabled = false;
    this.readOnly = false;
    this.required = false;
  }

  override createRenderRoot() {
    return this;
  }

  /** The text shown: the given value, else the element's own. */
  get text(): string {
    if (this.value !== undefined) return String(this.value == null ? '' : this.value);
    return this.own ?? '';
  }

  private get rows(): { min: number; max: number | null } {
    return { min: this.minRows || 3, max: this.maxRows === undefined ? 12 : this.maxRows };
  }

  private count(text: string): CharCountProps | null {
    if (this.maxLength == null) return null;
    return {
      id: `${this.areaId || this.autoId}-count`,
      count: text.length,
      max: this.maxLength,
      cautionAt: this.cautionAt,
      describe: this.describeCount,
      className: 'sdr-field__count',
    };
  }

  protected override willUpdate(changed: PropertyValues<this>) {
    super.willUpdate(changed);
    if (this.own === undefined) this.own = this.defaultValue == null ? '' : String(this.defaultValue);
  }

  protected override firstUpdated(changed: PropertyValues<this>) {
    super.firstUpdated(changed);
    this.watch();
  }

  protected override updated(changed: PropertyValues<this>) {
    super.updated(changed);
    this.fit();
    const count = this.count(this.text);
    if (count) this.counter.after(countLook(count), count.max, undefined);
    else this.counter.reset();
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this, { minWidth: '0px' });
    if (this.hasUpdated) this.watch();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.observer = undefined;
    this.counter.stop();
    this.hold?.();
    this.hold = undefined;
  }

  /** Fits again once the fonts are ready, and whenever the field's width changes. */
  private watch() {
    const el = this.area.value;
    if (document.fonts?.ready) document.fonts.ready.then(() => this.fit());
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.observer?.disconnect();
    let width = el.offsetWidth;
    this.observer = new ResizeObserver(() => {
      if (el.offsetWidth !== width) {
        width = el.offsetWidth;
        this.fit();
      }
    });
    this.observer.observe(el);
  }

  /** Measures a hidden twin of the same width, so the visible field never collapses while it is measured. */
  private fit() {
    const el = this.area.value;
    const twin = this.twin.value;
    if (!el || !twin || !this.isConnected) return;
    const { min, max } = this.rows;
    const cs = getComputedStyle(el);
    const line = Number.parseFloat(cs.lineHeight) || 22;
    const pad = Number.parseFloat(cs.paddingTop) + Number.parseFloat(cs.paddingBottom);
    const edge = Number.parseFloat(cs.borderTopWidth) + Number.parseFloat(cs.borderBottomWidth);
    twin.style.width = cs.width;
    twin.value = el.value || el.placeholder || ' ';
    const lo = line * min + pad;
    const hi = max ? line * Math.max(min, max) + pad : Number.POSITIVE_INFINITY;
    const need = twin.scrollHeight;
    el.style.height = `${Math.min(hi, Math.max(lo, need)) + edge}px`;
    el.style.overflowY = need > hi + 1 ? 'auto' : 'hidden';
  }

  private readonly onTextInput = (event: Event) => {
    if (this.value === undefined) {
      this.own = (event.target as HTMLTextAreaElement).value;
      this.onChange?.(event);
      return;
    }
    try {
      this.onChange?.(event);
    } finally {
      this.holdGiven();
    }
  };

  /**
   * Controlled: the field shows the given text again, whatever was typed, once the page has answered the edit.
   * React draws the page's answer and then restores the field, both at the end of the event. A Lit page answers
   * in its own updates, one microtask per level, so the field waits for the next frame (before it is painted):
   * an edit the page took is never written back (a write puts the caret at the end), one it refused is undone.
   */
  private holdGiven() {
    if (this.hold) return;
    const check = () => {
      this.hold = undefined;
      const area = this.area.value;
      if (area && this.value !== undefined && area.value !== this.text) this.requestUpdate();
    };
    if (typeof requestAnimationFrame === 'function') {
      const frame = requestAnimationFrame(check);
      this.hold = () => cancelAnimationFrame(frame);
    } else {
      const timer = setTimeout(check, 0);
      this.hold = () => clearTimeout(timer);
    }
  }

  override render() {
    const id = this.areaId || this.autoId;
    const text = this.text;
    const limit = this.maxLength;
    const over = limit != null && text.length > limit;
    const describedBy =
      [
        present(this.error) || present(this.hint) ? `${id}-msg` : null,
        limit != null ? `${id}-count` : null,
        this.describedBy,
      ]
        .filter(Boolean)
        .join(' ') || undefined;
    const count = this.count(text);
    return field(
      {
        id,
        label: this.label,
        hint: this.hint,
        error: this.error,
        optional: this.optional,
        size: this.size,
        className: cx(limit != null && 'sdr-field--counted', over && 'sdr-field--invalid', this.areaClass),
      },
      html`<div class="sdr-field__control sdr-field__control--area"
        ><textarea
          id=${id}
          rows=${this.rows.min}
          class="sdr-input sdr-textarea"
          name=${ifDefined(this.name)}
          placeholder=${ifDefined(this.placeholder)}
          ?disabled=${this.disabled}
          ?readonly=${this.readOnly}
          ?required=${this.required}
          autocomplete=${ifDefined(this.autocomplete)}
          aria-label=${ifDefined(this.ariaLabelText)}
          aria-invalid=${ifDefined(present(this.error) || over ? 'true' : undefined)}
          aria-describedby=${ifDefined(describedBy)}
          ${areaText(text)}
          @input=${this.onTextInput}
          @focus=${(e: FocusEvent) => this.onFocus?.(e)}
          @blur=${(e: FocusEvent) => this.onBlur?.(e)}
          @keydown=${(e: KeyboardEvent) => this.onKeydown?.(e)}
          ${ref(this.area)}
        ></textarea
        ><textarea
          class="sdr-input sdr-textarea sdr-textarea__shadow"
          aria-hidden="true"
          tabindex="-1"
          disabled
          rows="1"
          ${ref(this.twin)}
        ></textarea
      ></div
      >${count ? countView(count, countLook(count), this.counter.into) : null}`,
    );
  }
}

if (!customElements.get('sdr-char-count')) customElements.define('sdr-char-count', SdrCharCount);
if (!customElements.get('sdr-text-area')) customElements.define('sdr-text-area', SdrTextArea);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-char-count': SdrCharCount;
    'sdr-text-area': SdrTextArea;
  }
}
